import { useRef, useState } from 'react';
import { ImagePlus, Trash2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { materialService } from '../services/material-service';

// Vacío = rutas relativas, que el servidor de la pantalla reenvía al backend
// (ver vite.config.ts). Una URL fija a localhost no anda desde otra máquina.
const API_URL = import.meta.env.VITE_API_URL || '';

interface ImageUploadProps {
  materialId: string;
  currentImageUrl?: string | null;
  onImageChange: (imageUrl: string | null) => void;
}

export function ImageUpload({ materialId, currentImageUrl, onImageChange }: ImageUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resolvedImageUrl = preview || (currentImageUrl ? `${API_URL}${currentImageUrl}` : null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);

    // Local preview via FileReader
    const reader = new FileReader();
    reader.onload = (ev) => {
      setPreview(ev.target?.result as string);
    };
    reader.readAsDataURL(file);

    try {
      setIsUploading(true);
      const result = await materialService.uploadImage(materialId, file);
      setPreview(null); // let parent URL take over
      onImageChange(result.imageUrl);
    } catch {
      setError('Error al subir la imagen. Intente nuevamente.');
      setPreview(null);
    } finally {
      setIsUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    setError(null);
    try {
      setIsDeleting(true);
      await materialService.deleteImage(materialId);
      setPreview(null);
      onImageChange(null);
    } catch {
      setError('Error al eliminar la imagen.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
        Imagen del Producto
      </p>

      {resolvedImageUrl ? (
        <div className="relative w-24 h-24">
          <img
            src={resolvedImageUrl}
            alt="Imagen del producto"
            className="w-24 h-24 rounded-lg object-cover border border-border"
          />
          {(isUploading || isDeleting) && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-lg">
              <Loader2 className="h-6 w-6 animate-spin text-white" />
            </div>
          )}
          {!isUploading && !isDeleting && (
            <button
              type="button"
              onClick={handleDelete}
              className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 shadow hover:bg-destructive/90 transition-colors"
              title="Eliminar imagen"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={isUploading}
          className="flex flex-col items-center justify-center w-24 h-24 border-2 border-dashed border-border rounded-lg hover:border-primary hover:bg-muted/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Subir imagen"
        >
          {isUploading ? (
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          ) : (
            <>
              <ImagePlus className="h-6 w-6 text-muted-foreground" />
              <span className="text-xs text-muted-foreground mt-1">Subir</span>
            </>
          )}
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={handleFileChange}
      />

      {resolvedImageUrl && !isUploading && !isDeleting && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
          className="text-xs"
        >
          <ImagePlus className="h-3 w-3 mr-1" />
          Cambiar imagen
        </Button>
      )}

      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

export default ImageUpload;
